import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  arrayUnion,
  addDoc
} from 'firebase/firestore';
import {
  ShieldCheck,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function VerificationDashboard() {
  const { currentUser } = useAuth();
  const [isVerifier, setIsVerifier] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingReports, setPendingReports] = useState([]);
  const [myVerifications, setMyVerifications] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  // Check if user is a verifier
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const verifierRef = doc(db, 'verifiers', currentUser.uid);
    const checkVerifier = async () => {
      const verifierSnap = await getDoc(verifierRef);
      setIsVerifier(verifierSnap.exists());
      setLoading(false);
    };
    checkVerifier();
  }, [currentUser]);

  // Fetch pending reports (not verified, no rejected status)
  useEffect(() => {
    if (!isVerifier) return;

    const fetchPending = async () => {
      const q = query(collection(db, 'posts'), where('type', '==', 'CFE'), where('verified', '==', false));
      const snapshot = await getDocs(q);
      const reports = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(d => !d.verificationStatus || d.verificationStatus === 'pending')
        .sort((a, b) => b.createdAt - a.createdAt);
      setPendingReports(reports);
    };
    fetchPending();
  }, [isVerifier]);

  // Fetch my verification history
  useEffect(() => {
    if (!isVerifier || !currentUser) return;

    const fetchMyVerifications = async () => {
      const q = query(collection(db, 'posts'), where('type', '==', 'CFE'));
      const snapshot = await getDocs(q);
      const myReviews = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(d => d.verifications && d.verifications.some(v => v.verifierId === currentUser.uid))
        .sort((a, b) => b.createdAt - a.createdAt);
      setMyVerifications(myReviews);
    };
    fetchMyVerifications();
  }, [isVerifier, currentUser]);

  const handleVerification = async (reportId, status, notes = '') => {
    try {
      const reportRef = doc(db, 'posts', reportId);
      const verification = {
        verifierId: currentUser.uid,
        verifierEmail: currentUser.email,
        status: status, // 'approved' or 'rejected'
        notes: notes,
        timestamp: serverTimestamp()
      };

      // Add verification to array
      await updateDoc(reportRef, {
        verifications: arrayUnion(verification),
        verificationStatus: status === 'approved' ? 'pending' : 'rejected',
        lastVerificationUpdate: serverTimestamp()
      });

      // Check if report should be auto-verified (3+ approvals)
      const reportSnap = await getDoc(reportRef);
      const reportData = reportSnap.data();
      const verifications = reportData.verifications || [];
      const approvals = verifications.filter(v => v.status === 'approved').length;

      if (approvals >= 3) {
        await updateDoc(reportRef, {
          verified: true,
          verificationStatus: 'verified',
          verifiedAt: serverTimestamp()
        });

        // Add notification to report author
        const notificationsRef = collection(db, 'notifications');
        await addDoc(notificationsRef, {
          userId: reportData.authorId,
          type: 'report_verified',
          message: `Your report "${reportData.title}" has been verified by the community!`,
          postId: reportId,
          read: false,
          createdAt: serverTimestamp()
        });
      }

      alert(`Report ${status === 'approved' ? 'approved' : 'rejected'} successfully!`);
    } catch (error) {
      console.error('Error verifying report:', error);
      alert('Failed to submit verification. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center">
          <ShieldCheck size={64} className="mx-auto mb-4 text-cyan-400" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">Please log in to access the verification dashboard.</p>
        </div>
      </div>
    );
  }

  if (!isVerifier) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center">
          <Shield size={64} className="mx-auto mb-4 text-yellow-400" />
          <h2 className="text-2xl font-bold text-white mb-2">Verifier Access Required</h2>
          <p className="text-gray-400 mb-4">
            You need to be an approved expert verifier to access this dashboard.
          </p>
          <p className="text-sm text-gray-500">
            Expert verifiers are trusted community members who review and validate fraud reports.
            Contact an administrator to apply for verifier status.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck size={32} className="text-cyan-400" />
            <h1 className="text-3xl font-bold text-white">Verification Dashboard</h1>
          </div>
          <p className="text-gray-400">Review and verify community fraud reports</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'pending'
                ? 'glass-card border border-cyan-500/40 text-cyan-300'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock size={20} />
              Pending Reviews ({pendingReports.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'history'
                ? 'glass-card border border-cyan-500/40 text-cyan-300'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              My Verifications ({myVerifications.length})
            </div>
          </button>
        </div>

        {/* Content */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingReports.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                <h3 className="text-xl font-semibold text-white mb-2">All Caught Up!</h3>
                <p className="text-gray-400">No pending reports to review at the moment.</p>
              </div>
            ) : (
              pendingReports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  onVerify={handleVerification}
                  currentUserId={currentUser.uid}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {myVerifications.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold text-white mb-2">No Verifications Yet</h3>
                <p className="text-gray-400">Your verification history will appear here.</p>
              </div>
            ) : (
              myVerifications.map((report) => {
                const myVerification = report.verifications?.find(v => v.verifierId === currentUser.uid);
                return (
                  <HistoryCard
                    key={report.id}
                    report={report}
                    verification={myVerification}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Report Card Component for Pending Reviews
function ReportCard({ report, onVerify, currentUserId }) {
  const [showDetails, setShowDetails] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check if current user already verified this
  const alreadyVerified = report.verifications?.some(v => v.verifierId === currentUserId);
  const approvalCount = report.verifications?.filter(v => v.status === 'approved').length || 0;

  const handleSubmit = async (status) => {
    setSubmitting(true);
    await onVerify(report.id, status, notes);
    setSubmitting(false);
    setShowDetails(false);
    setNotes('');
  };

  return (
    <div className="glass-card rounded-xl p-6 border border-yellow-500/20 hover:border-yellow-500/40 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={20} className="text-yellow-400" />
            <h3 className="text-xl font-semibold text-white">{report.title}</h3>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
            <div className="flex items-center gap-1">
              <User size={14} />
              <span>
                0x{report.authorEmail?.substring(0, 4)}...{report.authorEmail?.substring(report.authorEmail.length - 4)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{report.createdAt ? new Date(report.createdAt.toDate()).toLocaleDateString() : 'Just now'}</span>
            </div>
            {approvalCount > 0 && (
              <div className="flex items-center gap-1 text-green-400">
                <CheckCircle size={14} />
                <span>{approvalCount} approval{approvalCount > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
          <p className="text-gray-300 mb-4 line-clamp-3">{report.content}</p>

          {report.sourceURL && (
            <a
              href={report.sourceURL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
            >
              <ExternalLink size={14} />
              View Source
            </a>
          )}
        </div>
      </div>

      {alreadyVerified ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
          <p className="text-green-400 text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            You already reviewed this report
          </p>
        </div>
      ) : (
        <>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full text-cyan-400 text-sm font-semibold hover:text-cyan-300 transition-colors"
          >
            {showDetails ? 'Hide Review Form' : 'Review This Report'}
          </button>

          {showDetails && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add review notes (optional)..."
                className="w-full glass-card rounded-lg p-3 text-white placeholder-gray-500 mb-4 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => handleSubmit('approved')}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-lg text-green-300 font-semibold hover:bg-green-500/30 transition-all disabled:opacity-50"
                >
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle size={18} />
                    Approve
                  </div>
                </button>
                <button
                  onClick={() => handleSubmit('rejected')}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 font-semibold hover:bg-red-500/30 transition-all disabled:opacity-50"
                >
                  <div className="flex items-center justify-center gap-2">
                    <XCircle size={18} />
                    Reject
                  </div>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// History Card Component
function HistoryCard({ report, verification }) {
  const isApproved = verification?.status === 'approved';

  return (
    <div className={`glass-card rounded-xl p-6 border ${isApproved ? 'border-green-500/20' : 'border-red-500/20'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {isApproved ? (
              <CheckCircle size={20} className="text-green-400" />
            ) : (
              <XCircle size={20} className="text-red-400" />
            )}
            <Link to={`/post/${report.id}`} className="text-lg font-semibold text-white hover:text-cyan-400">
              {report.title}
            </Link>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
            <span className={isApproved ? 'text-green-400' : 'text-red-400'}>
              {isApproved ? 'Approved' : 'Rejected'}
            </span>
            <span>
              {verification?.timestamp ?
                new Date(verification.timestamp.toDate()).toLocaleDateString() :
                'Recently'}
            </span>
          </div>
          {verification?.notes && (
            <p className="text-gray-400 text-sm mt-2 italic">"{verification.notes}"</p>
          )}
          {report.verified && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-green-300 text-sm">
              <ShieldCheck size={14} />
              Community Verified
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
